import { html } from 'lit'
import { customElement, query } from 'lit/decorators.js'
import { View } from '../view'
import { Symbols, inject } from '../../di'
import { DeviceListStore, DeviceStore, StatusStore } from '../../stores'
import { DeviceDownload } from './download'
import { DeviceWiredBackup } from './wired'

// Stroke icons at the mock's sizes; they inherit currentColor.
const icons = {
    plus: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`,
    info: html`<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`,
    version: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8"/></svg>`,
    clock: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>`,
    install: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`,
    update: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0115-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 01-15 6.7L3 16"/></svg>`,
    backup: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0018 0V5"/><path d="M3 12a9 3 0 0018 0"/></svg>`,
    book: html`<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>`,
    close: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
    warning: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`
}

const queueLimit = 3

@customElement('kob-device-list')
export class DevicesView extends View {

    @inject(Symbols.stores.deviceList)
    devicesList: DeviceListStore

    @inject(Symbols.stores.status)
    statusStore: StatusStore

    @query('kob-device-create')
    deviceCreateDialog

    @query('kob-device-download')
    deviceDownloadDialog: DeviceDownload

    @query('kob-device-wired-backup')
    deviceWiredBackupDialog: DeviceWiredBackup

    handleCreate = () => {
        this.deviceCreateDialog.deviceStore = this.devicesList.newStore()
        this.deviceCreateDialog.dialogOpened = true
    }

    handleEdit = (store: DeviceStore) => {
        this.deviceCreateDialog.deviceStore = store
        this.deviceCreateDialog.dialogOpened = true
    }

    handleInstall = (store: DeviceStore) => {
        this.deviceDownloadDialog.deviceStore = store
        this.deviceDownloadDialog.dialogOpened = true
    }

    handleBackup = (store: DeviceStore) => {
        this.deviceWiredBackupDialog.deviceStore = store
        this.deviceWiredBackupDialog.dialogOpened = true
    }

    render() {
        if (!this.devicesList.isInitialized)
            return html`
                <kob-loading></kob-loading>
            `

        if (this.devicesList.isNull)
            return html`<kob-device-null></kob-device-null>`

        const stores = this.devicesList.stores
        const count = stores.length
        const noun = count === 1 ? 'device' : 'devices'

        return html`
            <kob-device-create></kob-device-create>
            <kob-device-download></kob-device-download>
            <kob-device-wired-backup></kob-device-wired-backup>

            <section class="page-head">
                <div class="page-head-row">
                    <div class="page-eyebrow"><span class="num">02</span>Devices</div>
                    <div class="page-stat">
                        <strong>${count}</strong>
                        ${noun} · <span class="latest">latest ${this.statusStore.appVersion}</span>
                    </div>
                </div>
            </section>

            <div class="info-strip">
                <div class="info-banner">
                    ${icons.info}
                    <ul>
                        <li>Books you send arrive the next time your Kobo goes online — anything already on the device is skipped.</li>
                        <li>A restored book comes back in the same format you saved it in.</li>
                    </ul>
                </div>
            </div>

            <div class="toolbar">
                <span class="toolbar-count">Showing <strong>${count}</strong> ${noun}</span>
                <span class="toolbar-spacer"></span>
                <button class="btn btn-primary btn-sm" @click=${this.handleCreate}>
                    ${icons.plus}
                    Add Device
                </button>
            </div>

            <main class="devices" aria-label="Devices">
                <div class="device-list">
                    ${stores.map(store => this.renderDevice(store))}
                </div>
            </main>
        `
    }

    renderDevice = (store: DeviceStore) => {
        store.beginTimer(this)

        return html`
            <article class="device-row">
                <div class="device-main">
                    <div class="device-glyph ${store.isInstalled ? '' : 'off'}" aria-hidden="true"><span class="home"></span></div>
                    <div class="device-id">
                        <div class="name-row">
                            <button class="name" @click=${() => this.handleEdit(store)}>${store.name}</button>
                            <span class="model-badge">${store.typeLabel}</span>
                            ${this.renderStatus(store)}
                        </div>
                        <div class="device-meta">
                            ${store.isUnsupported
                                ? html`
                                    <span class="unsupported-note">
                                        ${icons.warning}
                                        This model's hardware can't run the Koblime sync app. Syncing won't work.
                                    </span>
                                `
                                : html`
                                    <span class="m">
                                        ${icons.version}
                                        ${store.isInstalled
                                            ? html`
                                                Koblime <strong>${store.version}</strong>
                                                ${store.isOutdated ? html`<span class="next">→ ${this.statusStore.appVersion}</span>` : ''}
                                            `
                                            : 'Not installed'
                                        }
                                    </span>
                                    <span class="m">${icons.clock}${store.lastSyncDisplay}</span>
                                `
                            }
                        </div>
                        ${store.isUnsupported ? '' : this.renderToggles(store)}
                    </div>
                    <div class="device-actions">
                        ${this.renderInstallAction(store)}
                        <button class="act" @click=${() => this.handleBackup(store)}>
                            ${icons.backup}
                            Wired Backup
                        </button>
                    </div>
                </div>
                ${this.renderQueue(store)}
            </article>
        `
    }

    renderStatus = (store: DeviceStore) => {
        const [theme, label] =
            store.isUnsupported ? ['badge-danger', 'Unsupported'] :
            !store.isInstalled ? ['badge-mute', 'Not installed'] :
            store.isOutdated ? ['badge-warning', 'Update available'] :
            ['badge-success', 'Up to date']

        return html`<span class="badge ${theme}"><span class="pulse"></span>${label}</span>`
    }

    renderInstallAction = (store: DeviceStore) => {
        if (store.isUnsupported)
            return html`<button class="act" disabled>${icons.install}Install</button>`

        const [theme, icon, label] =
            !store.isInstalled ? ['act primary', icons.install, 'Install'] :
            store.isOutdated ? ['act warn', icons.update, 'Update'] :
            ['act', icons.install, 'Reinstall']

        return html`
            <button class="${theme}" @click=${() => this.handleInstall(store)}>
                ${icon}
                ${label}
            </button>
        `
    }

    renderToggles = (store: DeviceStore) => html`
        <div class="device-toggles">
            ${this.renderSwitch('Silent UI', store.markedNoUI, checked => store.markNoUI(checked))}
            ${this.renderSwitch('Uninstall on sync', store.markedUninstall, checked => store.markUninstall(checked), 'danger')}
        </div>
    `

    renderSwitch = (label: string, checked: boolean, onChange: (checked: boolean) => void, theme = '') => html`
        <label class="switch ${theme}">
            <input type="checkbox" .checked=${checked} @change=${(e) => onChange(e.target.checked)} />
            <span class="track"></span>
            ${label}
        </label>
    `

    renderQueue = (store: DeviceStore) => {
        const docs = store.driveDocs || []
        const isFull = docs.length >= queueLimit

        const pickerCallback = async (data) => {
            if (data.action === google.picker.Action.PICKED)
                await store.addGoogleDoc(data.docs[0])
        }

        return html`
            <div class="device-queue">
                <div class="queue-head">
                    <span class="queue-label">Book Queue</span>
                    <span class="queue-count">${docs.length} / ${queueLimit} pending sync</span>
                </div>
                <div class="queue-items">
                    ${docs.map(doc => html`
                        <span class="queue-chip">
                            ${icons.book}
                            ${doc.name}
                            <button class="rm" title="Remove from queue" @click=${() => store.removeGoogleDoc(doc.id)}>
                                ${icons.close}
                            </button>
                        </span>
                    `)}
                    ${docs.length
                        ? ''
                        : html`<span class="queue-empty">${store.isInstalled
                            ? 'Nothing queued.'
                            : 'Install Koblime first, then queue books to push on the next sync.'
                        }</span>`
                    }
                    ${isFull
                        ? html`<span class="queue-full-note">Queue is full.</span>`
                        : html`<kob-drive-pick label="Add from Drive" .pickerCallback=${pickerCallback}></kob-drive-pick>`
                    }
                    ${!isFull
                        ? html`<label class="switch"><input type="checkbox" .checked=${store.markedKepubify} @change=${(e) => store.markKepubify(e.target.checked)} /><span class="track"></span>Prefer kepub</label>`
                        : ''
                    }
                </div>
            </div>
        `
    }
}
